// Global search / navigator in the top bar.
//
// Unlike the legacy OmniSearch (which only filtered the table), this jumps
// straight to the entity: pick an app, service, or environment and you land
// on its page. Pasting a deployment ID finds the matching record and opens
// its service page with the record highlighted; unknown free text falls back
// to the Activity feed as a search.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, Box, Chip, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useDeploymentData } from '../hooks/useDeploymentData';
import { serviceHref } from '../lib/records';
import { getColors } from '../theme/tokens';

interface NavOption {
  kind: 'app' | 'service' | 'env';
  label: string;
  sublabel?: string;
  path: string;
}

const KIND_META: Record<NavOption['kind'], { label: string; color: string }> = {
  app: { label: 'App', color: '#0075de' },
  service: { label: 'Service', color: '#8155b8' },
  env: { label: 'Env', color: '#dd5b00' },
};

const GlobalSearch: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const c = getColors(theme.palette.mode);
  const { staticRecords, uniqueApps, uniqueEnvs, records } = useDeploymentData();
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // "/" focuses search from anywhere (preserves the legacy shortcut).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== '/') return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const options = useMemo<NavOption[]>(() => {
    const out: NavOption[] = [];
    uniqueApps.forEach(app =>
      out.push({ kind: 'app', label: app, path: `/services/${encodeURIComponent(app)}` })
    );
    // Unique app/service pairs
    const seen = new Set<string>();
    staticRecords.forEach(r => {
      if (!r.app || !r.serviceName) return;
      const key = `${r.app}/${r.serviceName}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({
        kind: 'service',
        label: r.serviceName,
        sublabel: r.app,
        path: serviceHref(r.app, r.serviceName, r.type),
      });
    });
    uniqueEnvs.forEach(env =>
      out.push({ kind: 'env', label: env, path: `/environments/${encodeURIComponent(env)}` })
    );
    return out;
  }, [uniqueApps, uniqueEnvs, staticRecords]);

  const handleFreeText = (text: string) => {
    const q = text.trim();
    if (!q) return;
    // Deployment ID lookup against live records first. Match IDs by exact
    // equality — a substring match could route a typed identifier to an
    // unrelated service and skip the activity-search fallback.
    const lower = q.toLowerCase();
    const match = records.find(
      r =>
        r.deployment_id?.toLowerCase() === lower ||
        r.id?.toLowerCase() === lower
    );
    if (match && match.app && match.serviceName) {
      const href = serviceHref(match.app, match.serviceName, match.type);
      const dep = encodeURIComponent(match.deployment_id || match.id || '');
      navigate(`${href}${href.includes('?') ? '&' : '?'}dep=${dep}`);
    } else {
      navigate(`/activity?dep_id=${encodeURIComponent(q)}`);
    }
    setInputValue('');
  };

  return (
    <Autocomplete<NavOption, false, false, true>
      freeSolo
      size="small"
      options={options}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      value={null}
      blurOnSelect
      clearOnBlur
      onChange={(_, option) => {
        if (!option) return;
        if (typeof option === 'string') {
          handleFreeText(option);
          return;
        }
        navigate(option.path);
        setInputValue('');
      }}
      getOptionLabel={o => (typeof o === 'string' ? o : o.label)}
      filterOptions={(opts, state) => {
        const q = state.inputValue.toLowerCase().trim();
        if (!q) return opts.slice(0, 12);
        return opts
          .filter(
            o =>
              o.label.toLowerCase().includes(q) ||
              (o.sublabel || '').toLowerCase().includes(q)
          )
          .slice(0, 12);
      }}
      renderOption={(props, option) => {
        const meta = KIND_META[option.kind];
        return (
          <li {...props} key={`${option.kind}:${option.path}`}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Chip
                label={meta.label}
                size="small"
                sx={{
                  height: 18,
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  backgroundColor: `${meta.color}1a`,
                  color: meta.color,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
              <Typography variant="body2" noWrap>
                {option.sublabel ? (
                  <>
                    <Box component="span" sx={{ color: c.inkMuted }}>
                      {option.sublabel} /{' '}
                    </Box>
                    {option.label}
                  </>
                ) : (
                  option.label
                )}
              </Typography>
            </Box>
          </li>
        );
      }}
      sx={{ width: { xs: '100%', sm: 380, md: 460 } }}
      renderInput={params => (
        <TextField
          {...params}
          inputRef={inputRef}
          placeholder="Jump to app, service, env — or paste a deployment ID  ( / )"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <SearchIcon sx={{ fontSize: 18, color: c.inkFaint, ml: 0.5, mr: 0.5 }} />
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: c.canvasSoft,
              borderRadius: '6px',
              fontSize: '0.875rem',
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: c.hairline },
              '&.Mui-focused': { backgroundColor: c.surface },
            },
          }}
        />
      )}
    />
  );
};

export default GlobalSearch;
